import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { I18N_KEYS } from '@/language/keys';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { useUserWorkflowRole } from '@/state/workflow.state';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { useStringArrayView, useNodeStringArrayValue, useNodeInput } from '../Recipe/FlowContext';
import { hasEditingPermissions } from './Utils';
import { DynamicNode2 } from './DynamicNode/DynamicNode2';
import { TextInput } from './Shared/TextInput';
import { LIST_MAX_CHARS } from './consts/validations.consts';
import type { NodeId } from 'web';
import type { StringArrayData } from '@/types/node';

interface ArrayNodeProps {
  id: NodeId;
  data: StringArrayData;
}

export function ArrayNode({ id, data }: ArrayNodeProps) {
  const { t } = useTranslation();
  const view = useStringArrayView(id);
  const createRowId = useCallback(() => crypto.randomUUID(), []);

  const { delimiter, placeholder } = data;

  const handleItemChange = useCallback((index: number, value: string) => view.setItem(index, value), [view]);
  const handleItemDelete = useCallback(
    (index: number) => {
      setRowIds((prev) => prev.filter((_, i) => i !== index));
      view.deleteItem(index);
    },
    [view],
  );
  const handleItemAdd = useCallback(() => {
    setRowIds((prev) => [...prev, createRowId()]);
    view.addItem('');
  }, [createRowId, view]);
  const setDelimiter = useCallback((value: string) => view.setDelimiter(value), [view]);

  const role = useUserWorkflowRole();
  const editable = hasEditingPermissions(role, data);

  const currentArray = useNodeStringArrayValue(id);
  const inputValue = useNodeInput(id, 'text');
  const inputConnected = inputValue?.type === 'string';
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [pendingFocus, setPendingFocus] = useState<{ index: number; expectedLength: number } | null>(null);
  const missingRowIdsRef = useRef<Record<number, string>>({});
  const [rowIds, setRowIds] = useState<string[]>(() => currentArray.map(() => createRowId()));

  const getRowId = useCallback(
    (index: number) => {
      return rowIds[index] ?? (missingRowIdsRef.current[index] ??= createRowId());
    },
    [createRowId, rowIds],
  );

  useEffect(() => {
    // Only reconcile rowIds when the actual backing array length changes (i.e. the graph updated).
    // This prevents a transient "rowIds mismatch" during async updates from remounting inputs and stealing focus.
    if (rowIds.length !== currentArray.length) {
      setRowIds(currentArray.map(() => createRowId()));
      inputRefs.current = [];
      missingRowIdsRef.current = {};
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentArray.length]);

  const handleTabNavigate = useCallback(
    (index: number, direction: 'next' | 'prev') => {
      if (currentArray.length === 0) {
        return;
      }

      const jump = direction === 'next' ? 1 : -1;
      const nextIndex = (index + jump + currentArray.length) % currentArray.length;
      setPendingFocus({ index: nextIndex, expectedLength: currentArray.length });
    },
    [currentArray.length],
  );

  const handleEnterAfterSubmit = useCallback(
    (index: number, submittedValue: string) => {
      if (submittedValue.length === 0) {
        return;
      }
      const newArray = [...currentArray];
      newArray[index] = submittedValue;
      newArray.splice(index + 1, 0, '');

      setRowIds((prev) => {
        const next = [...prev];
        next.splice(index + 1, 0, createRowId());
        return next;
      });
      setPendingFocus({ index: index + 1, expectedLength: currentArray.length + 1 });
      view.setArray(newArray);
    },
    [createRowId, currentArray, view],
  );

  const handleEmptyBackspace = useCallback(
    (index: number) => {
      if (currentArray.length <= 1) {
        return;
      }

      const focusIndex = Math.max(0, index - 1);
      setPendingFocus({ index: focusIndex, expectedLength: currentArray.length - 1 });
      const newArray = [...currentArray];
      newArray.splice(index, 1);
      setRowIds((prev) => prev.filter((_, i) => i !== index));
      view.setArray(newArray);
    },
    [currentArray, view],
  );

  useLayoutEffect(() => {
    if (pendingFocus == null) {
      return;
    }
    if (currentArray.length !== pendingFocus.expectedLength) return;

    const el = inputRefs.current[pendingFocus.index];
    if (el) {
      el.focus();
      el.select();
      setPendingFocus(null);
    }
  }, [currentArray.length, pendingFocus]);

  return (
    <DynamicNode2 id={id} data={data} className="array" inputHandleYPos="30px" outputHandleYPos="30px">
      <FlexCol sx={{ gap: 1 }}>
        {inputConnected && (
          <FlexCenVer sx={{ gap: 1 }}>
            <TextSmRg text={t(I18N_KEYS.RECIPE_MAIN.NODES.ARRAY.SPLIT_TEXT_BY)} />
            <TextInput
              value={delimiter}
              onSubmit={(value: string) => void setDelimiter(value)}
              disabled={!editable}
              sx={{ width: '40px' }}
            />
          </FlexCenVer>
        )}
        {currentArray?.map((item, index) => (
          <ArrayRow
            key={getRowId(index)}
            index={index}
            value={item}
            itemsCount={currentArray.length}
            onSubmit={(index: number, value: string) => void handleItemChange(index, value)}
            onDelete={(index: number) => void handleItemDelete(index)}
            onEnterAfterSubmit={(value: string) => handleEnterAfterSubmit(index, value)}
            onEmptyBackspace={() => handleEmptyBackspace(index)}
            onTabNavigate={(direction: 'next' | 'prev') => handleTabNavigate(index, direction)}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            disabled={!editable || inputConnected}
            placeholder={placeholder || t(I18N_KEYS.RECIPE_MAIN.NODES.ARRAY.PLACEHOLDER)}
          />
        ))}
        {editable && !inputConnected && (
          <Box>
            <ButtonContained
              mode="text"
              size="small"
              onClick={() => void handleItemAdd()}
              sx={{ pointerEvents: data.isLocked ? 'none' : '' }}
            >
              {t(I18N_KEYS.RECIPE_MAIN.NODES.ARRAY.ADD_ITEM)}
            </ButtonContained>
          </Box>
        )}
      </FlexCol>
    </DynamicNode2>
  );
}

function ArrayRow({
  index,
  value,
  itemsCount,
  onSubmit,
  onDelete,
  onEnterAfterSubmit,
  onEmptyBackspace,
  onTabNavigate,
  inputRef,
  disabled,
  placeholder,
}: {
  index: number;
  value: string;
  itemsCount: number;
  onSubmit: (index: number, value: string) => void;
  onDelete: (index: number) => void;
  onEnterAfterSubmit: (value: string) => void;
  onEmptyBackspace: () => void;
  onTabNavigate: (direction: 'next' | 'prev') => void;
  inputRef: (el: HTMLInputElement | null) => void;
  disabled?: boolean;
  placeholder: string;
}) {
  const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);
  const disableDelete = itemsCount <= 1;

  return (
    <FlexCenVer sx={{ gap: 1 }}>
      <TextInput
        value={value}
        onSubmit={(value: string) => onSubmit(index, value)}
        onEnterAfterSubmit={onEnterAfterSubmit}
        onEmptyBackspace={disableDelete ? undefined : onEmptyBackspace}
        onTabNavigate={onTabNavigate}
        inputRef={inputRef}
        disabled={disabled}
        placeholder={placeholder}
        fullWidth
        inputProps={{ maxLength: LIST_MAX_CHARS }}
      />
      {!disabled && <AppXBtn onClick={handleDelete} size={32} />}
    </FlexCenVer>
  );
}
